import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { getStoredUser } from "../lib/auth";
import { useLanguage } from "../lib/language";

function AdminCoursesPage() {
  const user = getStoredUser();
  const { language } = useLanguage();
  const isArabic = language === "ar";

  const copy = isArabic
    ? {
        eyebrow: "إدارة الكورسات",
        title: "إدارة الكورسات",
        subtitle: "راجع كل كورس، المدرب المسؤول عنه، عدد التسليمات، ومتوسط الأداء والتقرير العام.",
        search: "ابحث عن كورس",
        searchPlaceholder: "اسم الكورس أو اسم المدرب",
        loading: "جار تحميل بيانات الكورسات...",
        empty: "لا توجد كورسات حاليًا.",
        selectHint: "اختر كورسًا من القائمة لعرض التفاصيل.",
        courses: "الكورسات",
        students: "الطلبة",
        submissions: "التسليمات",
        average: "متوسط الدرجات",
        assessments: "التقييمات",
        trainer: "المدرب",
        summary: "ملخص الكورس",
        aiReport: "تقرير الذكاء الاصطناعي",
        reportStrengths: "نقاط القوة",
        reportRisks: "نقاط تحتاج انتباهًا",
        reportRecommendations: "توصيات",
        noAssessments: "لا توجد تقييمات لهذا الكورس بعد.",
        noStudents: "لا توجد نتائج طلبة لهذا الكورس بعد."
      }
    : {
        eyebrow: "Course Management",
        title: "Course Management",
        subtitle: "Review each course, its owner, submission volume, performance, and the overall AI summary.",
        search: "Search courses",
        searchPlaceholder: "Course title or trainer name",
        loading: "Loading courses...",
        empty: "No courses found yet.",
        selectHint: "Select a course from the list to view details.",
        courses: "Courses",
        students: "Students",
        submissions: "Submissions",
        average: "Average score",
        assessments: "Assessments",
        trainer: "Trainer",
        summary: "Course summary",
        aiReport: "AI report",
        reportStrengths: "Strengths",
        reportRisks: "Risks",
        reportRecommendations: "Recommendations",
        noAssessments: "There are no assessments for this course yet.",
        noStudents: "There are no student results for this course yet."
      };

  const [courses, setCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [search, setSearch] = useState("");
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    async function fetchCourses() {
      if (!user?.id) {
        return;
      }
      try {
        setIsLoadingList(true);
        const response = await api.get("/admin/courses", { params: { admin_id: user.id } });
        if (!active) {
          return;
        }
        const nextCourses = response.data?.courses || [];
        setCourses(nextCourses);
        setSelectedCourseId((current) => current || nextCourses[0]?.id || null);
      } catch (requestError) {
        if (active) {
          setError(requestError.response?.data?.error || copy.loading);
        }
      } finally {
        if (active) {
          setIsLoadingList(false);
        }
      }
    }

    fetchCourses();
    return () => {
      active = false;
    };
  }, [copy.loading, user?.id]);

  useEffect(() => {
    let active = true;

    async function fetchCourseDetails() {
      if (!user?.id || !selectedCourseId) {
        setSelectedCourse(null);
        return;
      }
      try {
        setIsLoadingDetail(true);
        const response = await api.get(`/admin/courses/${selectedCourseId}`, {
          params: { admin_id: user.id }
        });
        if (active) {
          setSelectedCourse(response.data);
        }
      } catch (requestError) {
        if (active) {
          setError(requestError.response?.data?.error || copy.loading);
        }
      } finally {
        if (active) {
          setIsLoadingDetail(false);
        }
      }
    }

    fetchCourseDetails();
    return () => {
      active = false;
    };
  }, [copy.loading, selectedCourseId, user?.id]);

  const filteredCourses = useMemo(() => {
    const term = search.trim().toLowerCase();
    return courses.filter((course) => {
      if (!term) {
        return true;
      }
      return [course.title, course.trainer_name]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));
    });
  }, [courses, search]);

  const summary = useMemo(() => {
    return filteredCourses.reduce(
      (accumulator, course) => ({
        courses: accumulator.courses + 1,
        students: accumulator.students + Number(course.students_count || 0),
        submissions: accumulator.submissions + Number(course.submissions_count || 0)
      }),
      { courses: 0, students: 0, submissions: 0 }
    );
  }, [filteredCourses]);

  return (
    <section className="workspace-page admin-page">
      <div className="hero-panel">
        <div>
          <p className="section-eyebrow">{copy.eyebrow}</p>
          <h2 className="section-title">{copy.title}</h2>
          <p className="section-subtitle">{copy.subtitle}</p>
        </div>
      </div>

      <section className="metric-grid admin-summary-grid admin-summary-grid--compact">
        <article className="metric-card admin-stat-card">
          <span>{copy.courses}</span>
          <strong>{summary.courses}</strong>
        </article>
        <article className="metric-card admin-stat-card">
          <span>{copy.students}</span>
          <strong>{summary.students}</strong>
        </article>
        <article className="metric-card admin-stat-card">
          <span>{copy.submissions}</span>
          <strong>{summary.submissions}</strong>
        </article>
      </section>

      <div className="admin-management-layout">
        <div className="panel-card admin-management-list">
          <div className="field-stack">
            <label className="field-label">{copy.search}</label>
            <input
              className="text-input"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={copy.searchPlaceholder}
            />
          </div>

          {isLoadingList ? (
            <div className="state-panel state-panel--loading"><p>{copy.loading}</p></div>
          ) : !filteredCourses.length ? (
            <div className="state-panel state-panel--empty"><p>{copy.empty}</p></div>
          ) : (
            <div className="admin-record-stack">
              {filteredCourses.map((course) => (
                <button
                  key={course.id}
                  type="button"
                  className={selectedCourseId === course.id ? "admin-record-card admin-record-card--active" : "admin-record-card"}
                  onClick={() => setSelectedCourseId(course.id)}
                >
                  <div className="admin-record-card__header">
                    <strong>{course.title}</strong>
                    <span className="status-pill">{course.students_count || 0} {copy.students}</span>
                  </div>
                  <p className="helper-text">{copy.trainer}: {course.trainer_name}</p>
                  <div className="admin-record-card__meta">
                    <span>{copy.assessments}: {course.assessments_count || 0}</span>
                    <span>{copy.submissions}: {course.submissions_count || 0}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="panel-card admin-management-detail">
          {!selectedCourseId ? (
            <div className="state-panel state-panel--empty"><p>{copy.selectHint}</p></div>
          ) : isLoadingDetail || !selectedCourse ? (
            <div className="state-panel state-panel--loading"><p>{copy.loading}</p></div>
          ) : (
            <div className="stack-lg">
              <div className="card-title-row">
                <div>
                  <h3>{selectedCourse.course.title}</h3>
                  <p className="helper-text">{copy.trainer}: {selectedCourse.course.trainer_name}</p>
                </div>
                <span className="status-pill">{selectedCourse.summary.total_submissions || 0} {copy.submissions}</span>
              </div>

              <article className="admin-info-card">
                <h4>{copy.summary}</h4>
                <div className="two-column-grid">
                  <span>{copy.students}: {selectedCourse.summary.total_students || 0}</span>
                  <span>{copy.submissions}: {selectedCourse.summary.total_submissions || 0}</span>
                  <span>{copy.average}: {selectedCourse.summary.average_score || 0}</span>
                  <span>{isArabic ? "عدد المقارنات القبلي/البعدي" : "Pre/post comparisons"}: {selectedCourse.summary.compared_students || 0}</span>
                </div>
              </article>

              <div>
                <h4>{copy.assessments}</h4>
                {!selectedCourse.assessments.length ? (
                  <div className="state-panel state-panel--empty"><p>{copy.noAssessments}</p></div>
                ) : (
                  <div className="table-shell">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>{isArabic ? "العنوان" : "Title"}</th>
                          <th>{isArabic ? "النوع" : "Type"}</th>
                          <th>{copy.submissions}</th>
                          <th>{copy.average}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedCourse.assessments.map((assessment) => (
                          <tr key={assessment.id}>
                            <td>{assessment.title}</td>
                            <td>{assessment.type}</td>
                            <td>{assessment.submissions_count || 0}</td>
                            <td>{assessment.average_score ?? "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div>
                <h4>{isArabic ? "نتائج الطلبة" : "Student results"}</h4>
                {!selectedCourse.students.length ? (
                  <div className="state-panel state-panel--empty"><p>{copy.noStudents}</p></div>
                ) : (
                  <div className="table-shell">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>{isArabic ? "الطالب" : "Student"}</th>
                          <th>{isArabic ? "قبلي" : "Pre"}</th>
                          <th>{isArabic ? "بعدي" : "Post"}</th>
                          <th>{isArabic ? "التحسن" : "Improvement"}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedCourse.students.map((student) => (
                          <tr key={student.student_id}>
                            <td>{student.full_name}</td>
                            <td>{student.pre_score ?? "-"}</td>
                            <td>{student.post_score ?? "-"}</td>
                            <td>{student.improvement ?? "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <article className="admin-info-card">
                <h4>{copy.aiReport}</h4>
                <p>{selectedCourse.ai_report?.summary || "-"}</p>
                <div className="two-column-grid">
                  <div>
                    <strong>{copy.reportStrengths}</strong>
                    <ul className="plain-list">
                      {(selectedCourse.ai_report?.strengths || []).map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <strong>{copy.reportRisks}</strong>
                    <ul className="plain-list">
                      {(selectedCourse.ai_report?.risks || []).map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div>
                  <strong>{copy.reportRecommendations}</strong>
                  <ul className="plain-list">
                    {(selectedCourse.ai_report?.recommendations || []).map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </article>
            </div>
          )}
          {error && <div className="feedback-banner feedback-banner--error">{error}</div>}
        </div>
      </div>
    </section>
  );
}

export default AdminCoursesPage;
